import { COCLASSES } from '@/interfaces/coclass.interface'
import { CoreEntityDto, Purpose } from '@/interfaces/core.interface'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import { formatNumber } from '@/utils/math'

export type InfoData = {
  title?: string
  content?: string | undefined
  hasDivider?: boolean
  info?: string | undefined
  isBadge?: boolean
  poiProduct?: boolean
}
export function getCoClassDesc(code: string | undefined): string | undefined {
  if (!code) return undefined
  let desc = ''
  COCLASSES.find((item) => {
    if (item.coclass === code) {
      desc = item.description
    }
  })
  return desc
}
function getName(arr: any[] | undefined): string | undefined {
  if (arr && arr.length > 0) {
    arr.forEach((item, index) => {
      if (item.name) {
        arr[index] = item.name
      }
    })
    if (arr.length > 2) return arr.length.toString()
    return arr.join(', ')
  }
  return undefined
}
function getPurpose(purpose: Purpose[] | undefined, find: 'use' | 'type'): string | undefined {
  // find and return the purpose where mainuse is true
  if (!purpose) return undefined
  if (find === 'use') {
    if (purpose.length === 1) return purpose[0].use
    return purpose.find((item) => item.mainUse === true)?.use
  }
  if (find === 'type') {
    if (purpose.length === 1) return purpose[0].type
    return purpose.find((item) => item.mainUse === true)?.type
  }
}
function getStatus(status: string | undefined): string | undefined {
  const { t } = useI18n()
  if (status === 'FORTHCOMING') return t('general__text__upcoming')
  if (status === 'ACTIVE') return t('general__text__active')
  if (status === 'FINISHED') return t('general__text__discontinued')
  return undefined
}
function getYears(arr: number[] | undefined): string | undefined {
  const years: string[] = []
  if (arr) {
    arr.forEach((item) => {
      if (item) {
        years.push(item.toString())
      }
    })
    return years.join(', ')
  }
  return undefined
}

export function generateEntityInformation(currentEntity: CoreEntityDto) {
  const { t } = useI18n()
  const infoData: InfoData[] = []
  if (
    (currentEntity.type === 'COMPLEX' || currentEntity.type === 'REAL_ESTATE') &&
    currentEntity?.ownershipInfo?.owner?.name
  ) {
    infoData.push({
      title: t('general__text__legal-owner'),
      content: currentEntity.ownershipInfo?.owner?.name
    })
  }
  if (
    (currentEntity.type === 'COMPLEX' || currentEntity.type === 'REAL_ESTATE') &&
    currentEntity?.ownershipInfo?.assessedOwner?.name
  ) {
    infoData.push({
      title: t('general__text__assessed-owner'),
      content: currentEntity.ownershipInfo?.assessedOwner.name
    })
  }
  if (
    (currentEntity.type === 'COMPLEX' || currentEntity.type === 'REAL_ESTATE') &&
    currentEntity?.ownershipInfo?.landRightHolder?.name
  ) {
    infoData.push({
      title: t('general__text__land-right-holder'),
      content: currentEntity.ownershipInfo?.landRightHolder.name
    })
  }
  if (currentEntity.type === 'REAL_ESTATE' && currentEntity?.ownershipInfo?.ownershipStatus) {
    infoData.push({
      title: t('general__text__ownership-status'),
      content: getStatus(currentEntity?.ownershipInfo?.ownershipStatus),
      isBadge: true
    })
  }
  if (currentEntity?.ownershipInfo?.propertyManager?.length) {
    infoData.push({
      title: t('general__text__property-manager'), //will be added through admin ui -- if more than 2, return amount
      content: getName(currentEntity.ownershipInfo?.propertyManager)
    })
  }
  if (currentEntity?.ownershipInfo?.tenants?.length) {
    infoData.push({
      title: t('general__text__tenants'), //will be added through admin ui -- if more than 2, return amount
      content: getName(currentEntity?.ownershipInfo?.tenants)
    })
  }
  if (
    currentEntity.type === 'REAL_ESTATE' &&
    currentEntity?.authorityData?.assessedValue?.length &&
    currentEntity?.authorityData?.assessedValue[0].typeCode
  ) {
    infoData.push({
      title: t('general__text__type-code'),
      content: currentEntity.authorityData?.assessedValue[0].typeCode,
      hasDivider:
        currentEntity.ownershipInfo?.owner?.name ||
        currentEntity.ownershipInfo?.assessedOwner?.name ||
        currentEntity?.ownershipInfo?.landRightHolder?.name ||
        currentEntity?.ownershipInfo?.ownershipStatus ||
        currentEntity.ownershipInfo?.propertyManager ||
        currentEntity.ownershipInfo?.tenants
          ? true
          : false,
      info: t('info__type-code')
    })
  }
  if (
    currentEntity.type === 'REAL_ESTATE' &&
    currentEntity?.authorityData?.assessedValue?.length &&
    currentEntity?.authorityData?.assessedValue[0].assessedValue
  ) {
    infoData.push({
      title: t('general__text__real-estate-assessed-value'),
      content: formatNumber(currentEntity.authorityData.assessedValue[0].assessedValue, 0) + ' tkr',
      info: t('info__real-estate-assessed-value'),
      hasDivider:
        (currentEntity.ownershipInfo?.owner?.name ||
          currentEntity.ownershipInfo?.assessedOwner?.name ||
          currentEntity?.ownershipInfo?.landRightHolder?.name ||
          currentEntity?.ownershipInfo?.ownershipStatus ||
          currentEntity.ownershipInfo?.propertyManager ||
          currentEntity.ownershipInfo?.tenants) &&
        !currentEntity?.authorityData?.assessedValue[0]?.typeCode
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'CONSTRUCTION' &&
    currentEntity?.properties?.purchaseInfo?.yearOfPurchase
  ) {
    infoData.push({
      title: t('general__text__year-of-purchase'),
      content: currentEntity.properties?.purchaseInfo?.yearOfPurchase?.toString(),
      hasDivider:
        currentEntity.ownershipInfo?.propertyManager || currentEntity.ownershipInfo?.tenants
          ? true
          : false
    })
  }
  if (currentEntity.type === 'CONSTRUCTION' && currentEntity?.properties?.yearOfConstruction) {
    infoData.push({
      title: t('general__text__year-of-construction'),
      content: currentEntity.properties?.yearOfConstruction?.toString(),
      info: t('info__year-of-construction'),
      hasDivider:
        (currentEntity.ownershipInfo?.propertyManager || currentEntity.ownershipInfo?.tenants) &&
        !currentEntity?.properties?.purchaseInfo?.yearOfPurchase
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'CONSTRUCTION' &&
    currentEntity?.properties?.yearsOfExtension?.length
  ) {
    infoData.push({
      title: t('general__text__years-of-extension'),
      content: getYears(currentEntity?.properties?.yearsOfExtension),
      info: t('info__years-of-extension'),
      hasDivider:
        (currentEntity.ownershipInfo?.propertyManager || currentEntity.ownershipInfo?.tenants) &&
        !currentEntity?.properties?.yearOfConstruction &&
        !currentEntity?.properties?.purchaseInfo?.yearOfPurchase
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'CONSTRUCTION' &&
    currentEntity?.properties?.purpose &&
    getPurpose(currentEntity.properties?.purpose, 'type')
  ) {
    infoData.push({
      title: t('general__text__construction-type'),
      content: getPurpose(currentEntity.properties?.purpose, 'type'),
      info: t('info__construction-type'),
      hasDivider:
        currentEntity.ownershipInfo?.propertyManager ||
        currentEntity.ownershipInfo?.tenants ||
        currentEntity?.properties?.yearOfConstruction ||
        currentEntity?.properties?.purchaseInfo?.yearOfPurchase ||
        currentEntity?.properties?.yearsOfExtension?.length
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'CONSTRUCTION' &&
    currentEntity?.properties?.purpose &&
    getPurpose(currentEntity.properties?.purpose, 'use')
  ) {
    infoData.push({
      title: t('general__text__construction-purpose'),
      content: getPurpose(currentEntity.properties?.purpose, 'use'),
      info: t('info__construction-purpose'),
      hasDivider:
        (currentEntity.ownershipInfo?.propertyManager ||
          currentEntity.ownershipInfo?.tenants ||
          currentEntity?.properties?.yearOfConstruction ||
          currentEntity?.properties?.purchaseInfo?.yearOfPurchase ||
          currentEntity?.properties?.yearsOfExtension?.length) &&
        !currentEntity?.properties?.purpose &&
        !getPurpose(currentEntity.properties?.purpose, 'type')
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'SPACE' &&
    currentEntity?.properties?.classification?.PLANNED?.classCode
  ) {
    infoData.push({
      title: t('general__text__planned-room-type'),
      content: getCoClassDesc(currentEntity.properties?.classification?.PLANNED.classCode),
      hasDivider:
        currentEntity.ownershipInfo?.propertyManager || currentEntity.ownershipInfo?.tenants
          ? true
          : false
    })
  }
  if (
    currentEntity.type === 'SPACE' &&
    currentEntity?.properties?.classification?.USED?.classCode
  ) {
    infoData.push({
      title: t('general__text__used-room-type'),
      content: getCoClassDesc(currentEntity.properties?.classification?.USED.classCode),
      hasDivider:
        (currentEntity.ownershipInfo?.propertyManager || currentEntity.ownershipInfo?.tenants) &&
        !currentEntity?.properties?.classification?.PLANNED
          ? true
          : false
    })
  }
  if (currentEntity.type === 'SPACE' && currentEntity?.properties?.capacity) {
    infoData.push({
      title: t('general__data__room__capacity-only'),
      content: currentEntity.properties?.capacity.toString(),
      hasDivider:
        currentEntity.ownershipInfo?.propertyManager ||
        currentEntity.ownershipInfo?.tenants ||
        currentEntity?.properties?.classification?.PLANNED ||
        currentEntity?.properties?.classification?.USED
          ? true
          : false
    })
  }
  if (currentEntity.type === 'SPACE' && currentEntity?.properties?.bookable) {
    infoData.push({
      title: t('general__text__bookable'),
      content: currentEntity.properties?.bookable
        ? t('general__text__option-yes')
        : t('general__text__option-no'),
      hasDivider:
        (currentEntity.ownershipInfo?.propertyManager ||
          currentEntity.ownershipInfo?.tenants ||
          currentEntity?.properties?.classification?.PLANNED ||
          currentEntity?.properties?.classification?.USED) &&
        !currentEntity?.properties?.capacity
          ? true
          : false
    })
  }
  return infoData
}
function getPoiDescription(desc: string[]) {
  if (desc.length > 0) {
    return desc.join(', ')
  }
  return undefined
}
export function generatePoiInformation(poiReport: any): InfoData[] {
  const { t } = useI18n()
  if (!poiReport) return []
  const { selectedPoiProduct, descriptionList } = poiReport
  const infoData: InfoData[] = []
  if (descriptionList.value.length)
    infoData.push({
      title: t('general__text__information'),
      content: getPoiDescription(descriptionList.value)
    })
  if (selectedPoiProduct.value) {
    infoData.push({
      title: t('general__text__product-information'),
      content: selectedPoiProduct.value.title ? selectedPoiProduct.value.title : undefined,
      hasDivider: descriptionList.value.length ? true : false,
      poiProduct: true
    })
  }
  return infoData
}

export function generateLineInformation(): InfoData[] | any[] {
  const { t } = useI18n()
  const piniaStore = useGeneralStore()
  const line = piniaStore.selectedLine
  if (!line) return []
  const lineCategories = piniaStore.lineCategoriesForSelectedRealEstate
  const lineTypes = lineCategories.find((category) => category.id === line.categoryId)?.types
  const infoData: InfoData[] = []
  if (line?.name) {
    infoData.push({
      title: t('general__text__name'),
      content: line.name
    })
  }
  if (line.categoryId) {
    infoData.push({
      title: t('general__text__category'),
      content: lineCategories.find((category) => category.id === line.categoryId)?.name
    })
  }
  if (line?.typeId) {
    infoData.push({
      title: t('general__text__type'),
      content: lineTypes?.find((type) => type.id === line.typeId)?.name
    })
  }
  if (line?.width) {
    infoData.push({
      title: t('general__text__width'),
      content: line.width.toString() + ' m'
    })
  }
  return infoData
}

export function generateZoneInformation(): InfoData[] | any[] {
  const { t } = useI18n()
  const store = useStore()
  const piniaStore = useGeneralStore()
  const zone = piniaStore.selectedZone
  if (!zone) return []
  const selectedRealEstate = store.state.mapview.selectedRealEstate
  const zoneCategoriesForSelectedRealEstate =
    store.state.mapview.zoneCategoriesForSelectedRealEstate
  const zoneCategoriesForSelectedLevel = store.state.planview.zoneCategoriesForSelectedLevel
  const zoneCategories = selectedRealEstate
    ? zoneCategoriesForSelectedRealEstate
    : zoneCategoriesForSelectedLevel
  const zoneTypes = zoneCategories.find((category) => category.id === zone.categoryId)?.types
  if (!zone) return []
  const infoData: InfoData[] = []
  if (zone.name) {
    infoData.push({
      title: t('general__text__name'),
      content: zone.name
    })
  }
  if (zone.categoryId) {
    infoData.push({
      title: t('general__text__category'),
      content: zoneCategories.find((category) => category.id === zone.categoryId)?.name
    })
  }
  if (zone.typeId) {
    infoData.push({
      title: t('general__text__type'),
      content: zoneTypes?.find((type) => type.id === zone.typeId)?.name
    })
  }

  return infoData
}
